import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { logger } from "../../utils/logger";
import { AnnualPlanServices, CardDetails, OfficeActivePlan, OfficeAnnualPlan, Subscription, SubscriptionType } from "../../models/annual-plan.model";
import { AnnualPlanService } from "../../services/annualPlan.service";
import { getUser } from "./CurrentUserSlice";
import { AnnualPlanNotificationResponse } from "../../models/annual-plan.model";

const log = logger.getLogger("AnnualPlanErrors");

export interface AnnualPlanTypeState {
  error: boolean;
  loading: boolean;
  annualPlans: OfficeAnnualPlan[];
  annualPlanServices: AnnualPlanService[];
  subscriptionsMonthly: Subscription[];
  subscriptionsYearly: Subscription[];
  cardDetails: CardDetails | undefined;
  annualPlanNotifications: AnnualPlanNotificationResponse | undefined;
}

const initialState: AnnualPlanTypeState = {
  loading: false,
  error: false,
  annualPlans: [],
  annualPlanServices: [],
  subscriptionsMonthly: [],
  subscriptionsYearly: [],
  cardDetails: undefined,
  annualPlanNotifications: undefined,
};

export const getOfficeAnnualPlans = createAsyncThunk(
  "annualPlanApi/getOfficeAnnualPlans",
  async ({ officeId }: { officeId: string }, { rejectWithValue }) => {
    try {
      const data = await AnnualPlanService.getOfficeAnnualPlans(officeId);
      return data;
    } catch (err) {
      log.warn(JSON.stringify(err));
      return rejectWithValue(err);
    }
  }
);

export const getOfficeAnnualPlanServices = createAsyncThunk(
  "annualPlanApi/getOfficeAnnualPlanServices",
  async ({ officeId }: { officeId: string }, { rejectWithValue }) => {
    try {
      const data = await AnnualPlanService.getOfficeAnnualPlanServices(officeId);
      return data;
    } catch (err) {
      log.warn(JSON.stringify(err));
      return rejectWithValue(err);
    }
  }
);

export const getMonthlySubscriptions = createAsyncThunk(
  "annualPlanApi/getMonthlySubscriptions",
  async ({ officeId }: { officeId: string }, { rejectWithValue }) => {
    try {
      const data = await AnnualPlanService.getSubscriptions(officeId, SubscriptionType.Monthly);
      return data;
    } catch (err) {
      log.warn(JSON.stringify(err));
      return rejectWithValue(err);
    }
  }
);

export const getYearlySubscriptions = createAsyncThunk(
  "annualPlanApi/getYearlySubscriptions",
  async ({ officeId }: { officeId: string }, { rejectWithValue }) => {
    try {
      const data = await AnnualPlanService.getSubscriptions(officeId, SubscriptionType.Yearly);
      return data;
    } catch (err) {
      log.warn(JSON.stringify(err));
      return rejectWithValue(err);
    }
  }
);

export const updateOfficePlan = createAsyncThunk(
  "annualPlanApi/updateOfficePlan",
  async ({ officeId, body }: { officeId: string; body: OfficeActivePlan }, { rejectWithValue }) => {
    try {
      const data = await AnnualPlanService.updateOfficePlan(officeId, body);
      return data;
    } catch (err) {
      log.warn(JSON.stringify(err));
      return rejectWithValue(err);
    }
  }
);

export const updateOfficeAnnualPlanServices = createAsyncThunk(
  "annualPlanApi/updateOfficePlan",
  async ({ officeId, body }: { officeId: string; body: AnnualPlanServices[] }, { rejectWithValue }) => {
    try {
      const data = await AnnualPlanService.updateOfficeAnnualPlanServices(officeId, body);
      return data;
    } catch (err) {
      log.warn(JSON.stringify(err));
      return rejectWithValue(err);
    }
  }
);

export const getSubscriptionPaymentIntent = createAsyncThunk(
  "annualPlanApi/getSubscriptionPaymentIntent",
  async (
    { officeId, userId, planId, additionalMembers }: { officeId: string; userId: string; planId: string; additionalMembers?: string },
    { rejectWithValue }
  ) => {
    try {
      const data = await AnnualPlanService.getSubscriptionPaymentIntent(planId, userId, officeId, additionalMembers);
      return data;
    } catch (err) {
      log.warn(JSON.stringify(err));
      return undefined;
    }
  }
);

export const getUpdateSubscriptionPaymentIntent = createAsyncThunk(
  "annualPlanApi/getSubscriptionPaymentIntent",
  async (
    {
      officeId,
      userId,
      planId,
      additionalMembers,
      newPlanID,
    }: { officeId: string; userId: string; planId: string; additionalMembers?: string; newPlanID?: string },
    { rejectWithValue }
  ) => {
    try {
      const data = await AnnualPlanService.getUpdateSubscriptionPaymentIntent(planId, userId, officeId, additionalMembers, newPlanID);
      return data;
    } catch (err) {
      log.warn(JSON.stringify(err));
      return undefined;
    }
  }
);

export const getValidateSubscribeUpdate = createAsyncThunk(
  "annualPlanApi/getValidateSubscribeUpdate",
  async (
    {
      officeId,
      userId,
      planId,
      additionalMembers,
      newPlanID,
    }: { officeId: string; userId: string; planId: string; additionalMembers?: string; newPlanID?: string },
    { rejectWithValue }
  ) => {
    try {
      const data = await AnnualPlanService.getValidateSubscribeUpdate(planId, userId, officeId, additionalMembers, newPlanID);
      return data;
    } catch (err) {
      log.warn(JSON.stringify(err));
      return undefined;
    }
  }
);

export const getPaymentUpgrade = createAsyncThunk(
  "annualPlanApi/getPaymentUpgrade",
  async (
    { officeId, userId, proratedAmount, proratedDate }: { officeId: string; userId: string; proratedAmount?: number; proratedDate?: number },
    { rejectWithValue }
  ) => {
    try {
      const data = await AnnualPlanService.getPaymentUpgrade(officeId, userId, proratedAmount, proratedDate);
      return data;
    } catch (err) {
      log.warn(JSON.stringify(err));
      return undefined;
    }
  }
);

export const completePatientSubscription = createAsyncThunk(
  "annualPlanApi/getSubscriptionPaymentIntent",
  async (
    {
      officeId,
      userId,
      planId,
      stripeSubscriptionId,
      paymentId,
    }: { officeId: string; userId: string; planId: string; stripeSubscriptionId: string; paymentId: string },
    { dispatch }
  ) => {
    try {
      const data = await AnnualPlanService.completePatientSubscription(planId, userId, officeId, stripeSubscriptionId, paymentId);
      await dispatch(getUser());
      return data;
    } catch (err) {
      log.warn(JSON.stringify(err));
      return undefined;
    }
  }
);

export const getReceiptData = createAsyncThunk(
  "annualPlanApi/getReceiptData",
  async ({ officeId, userId, userAnnualPlanId }: { officeId: string; userId: string; userAnnualPlanId: any }, { dispatch }) => {
    try {
      const data = await AnnualPlanService.getReceiptData(userAnnualPlanId, userId, officeId);
      return data;
    } catch (err) {
      log.warn(JSON.stringify(err));
      return undefined;
    }
  }
);

export const cancelAnnualPlan = createAsyncThunk(
  "annualPlanApi/cancelAnnualPlan",
  async (
    { officeId, userId, userAnnualPlanId, reason }: { officeId: string; userId: string; userAnnualPlanId: string; reason: string },
    { dispatch }
  ) => {
    try {
      const data = await AnnualPlanService.cancelAnnualPlan(userAnnualPlanId, userId, officeId, reason);
      if (data) await dispatch(getUser());
      return data;
    } catch (err) {
      log.warn(JSON.stringify(err));
      return undefined;
    }
  }
);

export const uploadStripeReceipt = createAsyncThunk(
  "annualPlanApi/cancelAnnualPlan",
  async ({ fileID, userId, userAnnualPlanId }: { fileID: string; userId: string; userAnnualPlanId: string }, { dispatch }) => {
    try {
      const data = await AnnualPlanService.uploadStripeReceipt(userAnnualPlanId, userId, fileID);
      if (data) await dispatch(getUser());
      return data;
    } catch (err) {
      log.warn(JSON.stringify(err));
      return undefined;
    }
  }
);

export const getCardDetails = createAsyncThunk(
  "annualPlanApi/getCardDetails",
  async ({ officeId, userId, userAnnualPlanId }: { officeId: string; userId: string; userAnnualPlanId: string }, { dispatch }) => {
    try {
      const data = await AnnualPlanService.getCardDetails(userAnnualPlanId, userId, officeId);
      return data;
    } catch (err) {
      log.warn(JSON.stringify(err));
      return undefined;
    }
  }
);

export const updateFamilyMembers = createAsyncThunk(
  "annualPlanApi/updateFamilyMembers",
  async (
    { officeId, userId, userAnnualPlanId, family }: { officeId: string; userId: string; userAnnualPlanId: string; family: string },
    { dispatch }
  ) => {
    try {
      const data = await AnnualPlanService.updateFamilyMembers(userAnnualPlanId, userId, officeId, family);
      if (data) dispatch(getUser());
      return data;
    } catch (err) {
      log.warn(JSON.stringify(err));
      return undefined;
    }
  }
);

export const updatePaymentMethod = createAsyncThunk(
  "annualPlanApi/updatePaymentMethod",
  async (
    { officeId, userId, paymentMethodId, userAnnualPlanId }: { officeId: string; userId: string; paymentMethodId: string; userAnnualPlanId: string },
    { dispatch }
  ) => {
    try {
      const data = await AnnualPlanService.updatePaymentMethod(userId, officeId, paymentMethodId);
      await dispatch(getCardDetails({ officeId, userAnnualPlanId, userId }));
      return data;
    } catch (err) {
      log.warn(JSON.stringify(err));
      return undefined;
    }
  }
);

export const getAnnualPlanNotifications = createAsyncThunk(
  "annualPlanApi/getAnnualPlanNotifications",
  async ({ officeId, archived }: { officeId: string; archived?: boolean }) => {
    try {
      const data = await AnnualPlanService.getAnnualPlanNotifications(officeId, archived);
      return data;
    } catch (err) {
      log.warn(JSON.stringify(err));
      return undefined;
    }
  }
);

export const archiveAnnualPlanNotification = createAsyncThunk(
  "annualPlanApi/archiveAnnualPlanNotification",
  async ({ officeId, notificationId, archive }: { officeId: string; notificationId: string; archive: boolean }) => {
    try {
      const data = await AnnualPlanService.archiveAnnualPlanNotification(officeId, notificationId, archive);
      return data;
    } catch (err) {
      log.warn(JSON.stringify(err));
      return undefined;
    }
  }
);

export const AnnualPlanSlice = createSlice({
  name: "annualPlanApi",
  initialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(getOfficeAnnualPlans.pending, state => {
        state.error = false;
        state.loading = true;
      })
      .addCase(getOfficeAnnualPlans.fulfilled, (state, action: PayloadAction<OfficeAnnualPlan[]>) => {
        state.error = false;
        state.loading = false;
        state.annualPlans = action.payload;
      })
      .addCase(getOfficeAnnualPlans.rejected, state => {
        state.error = true;
        state.loading = false;
      })
      .addCase(getMonthlySubscriptions.fulfilled, (state, action: PayloadAction<Subscription[]>) => {
        state.subscriptionsMonthly = action.payload;
      })
      .addCase(getYearlySubscriptions.fulfilled, (state, action: PayloadAction<Subscription[]>) => {
        state.subscriptionsYearly = action.payload;
      })
      .addCase(getOfficeAnnualPlanServices.fulfilled, (state, action: PayloadAction<AnnualPlanService[]>) => {
        state.annualPlanServices = action.payload;
      })
      .addCase(getCardDetails.fulfilled, (state, action: PayloadAction<CardDetails | undefined>) => {
        state.cardDetails = action.payload;
      })
      .addCase(getAnnualPlanNotifications.fulfilled, (state, action: PayloadAction<any>) => {
        state.annualPlanNotifications = action.payload;
      })
      .addCase(archiveAnnualPlanNotification.fulfilled, (state, action: PayloadAction<any>) => {
        state.annualPlanNotifications = action.payload;
      });
  },
});

export default AnnualPlanSlice.reducer;
